import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import TaskService from "./TaskService";
import "./TicketAgeingReportScreen.css";

const AGING_BUCKETS = [
  {
    code: "LT_7",
    label: "< 7 days",
    displayLabel: "Under 7 days",
    description: "Recently opened",
    shortLabel: "< 7d",
    className: "age-bucket--lt7",
    warn: false,
    icon: "fa-bolt",
  },
  {
    code: "LT_30",
    label: "< 30 days",
    displayLabel: "Under 30 days",
    description: "Still fresh",
    shortLabel: "< 30d",
    className: "age-bucket--lt30",
    warn: false,
    icon: "fa-clock",
  },
  {
    code: "LT_45",
    label: "< 45 Days",
    displayLabel: "Under 45 days",
    description: "Approaching mid-age",
    shortLabel: "< 45d",
    className: "age-bucket--lt45",
    warn: false,
    icon: "fa-hourglass-half",
  },
  {
    code: "LT_60",
    label: "< 60 days",
    displayLabel: "Under 60 days",
    description: "Mid-aged tickets",
    shortLabel: "< 60d",
    className: "age-bucket--lt60",
    warn: false,
    icon: "fa-hourglass-end",
  },
  {
    code: "LT_90",
    label: "< 90 Days",
    displayLabel: "Under 90 days",
    description: "Aging — review soon",
    shortLabel: "< 90d",
    className: "age-bucket--lt90",
    warn: true,
    icon: "fa-exclamation-circle",
  },
  {
    code: "LT_180",
    label: "< 180 days",
    displayLabel: "Under 180 days",
    description: "Old — needs attention",
    shortLabel: "< 180d",
    className: "age-bucket--lt180",
    warn: true,
    icon: "fa-exclamation-triangle",
  },
  {
    code: "GT_180",
    label: "> 180 days",
    displayLabel: "Over 180 days",
    description: "Critical backlog",
    shortLabel: "> 180d",
    className: "age-bucket--gt180",
    warn: true,
    icon: "fa-fire",
  },
];

function parseLookupFromStorage(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

const DEFAULT_STATUS_PATTERNS = [
  /^in\s*progress$/i,
  /^not\s*started$/i,
  /^in\s*[- ]?\s*testing$/i,
];

function resolveDefaultStatuses(statusList) {
  if (!Array.isArray(statusList) || !statusList.length) return [];

  const names = statusList
    .map((status) => status?.aa_app_rl_task_status_name)
    .filter(Boolean);

  const resolved = [];
  for (const pattern of DEFAULT_STATUS_PATTERNS) {
    const match = names.find((name) => pattern.test(String(name).trim()));
    if (match && !resolved.includes(match)) {
      resolved.push(match);
    }
  }
  return resolved;
}

const DEFAULT_TASK_TYPE_PATTERNS = [/^bug$/i, /^enhancement$/i];

function getTaskTypeFilterValue(taskType) {
  const shortName = String(taskType?.aa_app_rl_task_type_shortname || "").trim();
  const longName = String(taskType?.aa_app_rl_task_type_longname || "").trim();
  const typeId = taskType?.aa_app_rl_task_type_id;
  return shortName || longName || (typeId != null ? String(typeId) : "");
}

function resolveDefaultTaskTypes(taskTypeList) {
  if (!Array.isArray(taskTypeList) || !taskTypeList.length) return [];

  const resolved = [];
  for (const pattern of DEFAULT_TASK_TYPE_PATTERNS) {
    const match = taskTypeList.find((taskType) => {
      const shortName = String(taskType?.aa_app_rl_task_type_shortname || "").trim();
      const longName = String(taskType?.aa_app_rl_task_type_longname || "").trim();
      return pattern.test(shortName) || pattern.test(longName);
    });
    if (!match) continue;

    const value = getTaskTypeFilterValue(match);
    if (value && !resolved.includes(value)) {
      resolved.push(value);
    }
  }
  return resolved;
}

function formatDate(dateStr) {
  if (!dateStr) return "N/A";
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

function getBucketClassForDays(days) {
  const n = Number(days);
  if (Number.isNaN(n)) return "";
  if (n <= 7) return "age-bucket--lt7";
  if (n <= 30) return "age-bucket--lt30";
  if (n <= 45) return "age-bucket--lt45";
  if (n <= 60) return "age-bucket--lt60";
  if (n <= 90) return "age-bucket--lt90";
  if (n <= 180) return "age-bucket--lt180";
  return "age-bucket--gt180";
}

function aggregateBucketTotals(summaryRows) {
  const totals = Object.fromEntries(AGING_BUCKETS.map((b) => [b.code, 0]));
  let totalTickets = 0;
  for (const row of summaryRows) {
    for (const bucket of AGING_BUCKETS) {
      totals[bucket.code] += Number(row[bucket.code] || 0);
    }
    totalTickets += Number(row.total_tickets || 0);
  }
  return { totals, totalTickets };
}

function getPriorityClass(priority) {
  const p = String(priority || "").toLowerCase();
  if (p.includes("critical") || p.includes("urgent")) return "critical";
  if (p.includes("high")) return "high";
  if (p.includes("medium") || p.includes("med")) return "medium";
  if (p.includes("low")) return "low";
  return "default";
}

function getInitials(name) {
  if (!name || name === "Unassigned") return "?";
  const parts = String(name).split(/[,\s]+/).filter(Boolean);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
}

function getAssigneeNames(name) {
  if (!name || name === "Unassigned") return [];
  return String(name)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function getAgeRingProgress(days) {
  const n = Number(days);
  if (Number.isNaN(n)) return 0;
  return Math.min(100, Math.round((n / 180) * 100));
}

function getBucketLabelForDays(days) {
  const n = Number(days);
  if (Number.isNaN(n)) return "Unknown";
  const bucket = AGING_BUCKETS.find((b) => {
    if (b.code === "GT_180") return n > 180;
    const limit = Number(b.code.replace("LT_", ""));
    return n <= limit;
  });
  return bucket?.displayLabel || bucket?.label || "Unknown";
}

function getBucketPercent(count, total) {
  const t = Number(total);
  if (!t) return 0;
  return Math.round((Number(count) / t) * 100);
}

/**
 * Shared age palette (spectrum donut/lanes + heatmap cells/headers):
 * light green → medium green → light yellow → medium yellow →
 * light orange → dark orange → dark red
 */
const BUCKET_COLORS = {
  LT_7: "#bbf7d0", // light green
  LT_30: "#4ade80", // medium green
  LT_45: "#fef08a", // light yellow
  LT_60: "#facc15", // medium yellow
  LT_90: "#fdba74", // light orange
  LT_180: "#ea580c", // dark orange
  GT_180: "#b91c1c", // dark red
};

const HEATMAP_BUCKET_COLORS = BUCKET_COLORS;

/** White text on deeper orange / red fills */
const HEATMAP_BUCKET_USE_LIGHT_TEXT = {
  LT_7: false,
  LT_30: false,
  LT_45: false,
  LT_60: false,
  LT_90: false,
  LT_180: true,
  GT_180: true,
};

function getHeatmapBucketColor(bucketCode) {
  return HEATMAP_BUCKET_COLORS[bucketCode] || "#94a3b8";
}

function getAppMonogram(name) {
  if (!name) return "?";
  const words = String(name).trim().split(/\s+/);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0].charAt(0)}${words[1].charAt(0)}`.toUpperCase();
}

function HeatmapAppName({ name }) {
  const nameRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = nameRef.current;
    if (!el) return undefined;

    const checkTruncation = () => {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    };

    checkTruncation();

    let resizeObserver;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(checkTruncation);
      resizeObserver.observe(el);
    }

    window.addEventListener("resize", checkTruncation);
    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", checkTruncation);
    };
  }, [name]);

  return (
    <span
      ref={nameRef}
      className="aging-heatmap__app-name"
      title={isTruncated ? name : undefined}
    >
      {name}
    </span>
  );
}

function toggleArrayValue(values, value) {
  const v = String(value);
  return values.includes(v) ? values.filter((item) => item !== v) : [...values, v];
}

function MultiSelectFilter({
  id,
  label,
  icon,
  options = [],
  selectedValues = [],
  onChange,
  placeholder = "All",
  chips = [],
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const allOptionValues = useMemo(
    () => options.map((opt) => String(opt.value)),
    [options]
  );

  const allSelected =
    allOptionValues.length > 0 &&
    allOptionValues.every((value) => selectedValues.includes(value));

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  const summary = useMemo(() => {
    if (!selectedValues.length) return placeholder;
    if (allOptionValues.length > 0 && allSelected) return placeholder;
    if (selectedValues.length === 1) {
      const match = options.find((opt) => String(opt.value) === selectedValues[0]);
      return match?.label || "1 selected";
    }
    return `${selectedValues.length} selected`;
  }, [selectedValues, options, placeholder, allOptionValues, allSelected]);

  const handleSelectAll = () => {
    onChange(allSelected ? [] : [...allOptionValues]);
  };

  return (
    <div className="aging-multi-select" ref={rootRef}>
      <label htmlFor={id}>
        {icon ? <i className={`fas ${icon}`} aria-hidden="true" /> : null}
        {label}
      </label>
      <button
        type="button"
        id={id}
        className={`aging-multi-select__trigger${open ? " aging-multi-select__trigger--open" : ""}${
          selectedValues.length && !allSelected ? " aging-multi-select__trigger--has-value" : ""
        }`}
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className="aging-multi-select__summary">{summary}</span>
        <i className={`fas fa-chevron-${open ? "up" : "down"}`} aria-hidden="true" />
      </button>
      {open ? (
        <div className="aging-multi-select__menu" role="listbox" aria-multiselectable="true">
          <div className="aging-multi-select__menu-head">
            <span>Select one or more</span>
            {selectedValues.length > 0 ? (
              <button type="button" className="aging-multi-select__clear" onClick={() => onChange([])}>
                Clear
              </button>
            ) : null}
          </div>
          <div className="aging-multi-select__options">
            {allOptionValues.length > 0 ? (
              <label
                className={`aging-multi-select__option aging-multi-select__option--select-all${
                  allSelected ? " aging-multi-select__option--checked" : ""
                }`}
              >
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={handleSelectAll}
                />
                <span>Select all</span>
              </label>
            ) : null}
            {options.map((opt) => {
              const value = String(opt.value);
              const checked = selectedValues.includes(value);
              return (
                <label key={value} className={`aging-multi-select__option${checked ? " aging-multi-select__option--checked" : ""}`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onChange(toggleArrayValue(selectedValues, value))}
                  />
                  <span>{opt.label}</span>
                </label>
              );
            })}
          </div>
        </div>
      ) : null}
      <div className="aging-multi-select__chips" aria-live="polite">
        {chips.map((chip) => (
          <span key={chip.key} className="aging-filter-chip">
            {chip.label}
            <button
              type="button"
              className="aging-filter-chip__remove"
              onClick={chip.onRemove}
              aria-label={`Remove ${chip.label}`}
            >
              <i className="fas fa-times" aria-hidden="true" />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}

function buildApiFilters(applicationIds, statuses, agingBuckets, taskTypes, heatmapDrillDown) {
  const filters = {};
  if (heatmapDrillDown) {
    filters.applicationId = heatmapDrillDown.appId;
    filters.agingBucket = heatmapDrillDown.bucketCode;
  } else {
    if (applicationIds.length === 1) {
      filters.applicationId = applicationIds[0];
    } else if (applicationIds.length > 1) {
      filters.applicationIds = applicationIds;
    }
    if (agingBuckets.length === 1) {
      filters.agingBucket = agingBuckets[0];
    } else if (agingBuckets.length > 1) {
      filters.agingBuckets = agingBuckets;
    }
  }
  if (statuses.length === 1) {
    filters.status = statuses[0];
  } else if (statuses.length > 1) {
    filters.statuses = statuses;
  }
  if (taskTypes.length > 0) {
    filters.taskTypes = taskTypes;
  }
  return filters;
}

const TicketAgeingReportScreen = () => {
  const navigate = useNavigate();
  const initialStatusList = useMemo(() => parseLookupFromStorage("task_status_details"), []);
  const initialDefaultStatuses = useMemo(
    () => resolveDefaultStatuses(initialStatusList),
    [initialStatusList]
  );
  const initialTaskTypeList = useMemo(() => parseLookupFromStorage("task_type_details"), []);
  const initialDefaultTaskTypes = useMemo(
    () => resolveDefaultTaskTypes(initialTaskTypeList),
    [initialTaskTypeList]
  );
  const [summaryRows, setSummaryRows] = useState([]);
  const [detailRows, setDetailRows] = useState([]);
  const [applications, setApplications] = useState([]);
  const [statuses, setStatuses] = useState(initialStatusList);
  const [taskTypes, setTaskTypes] = useState(() => parseLookupFromStorage("task_type_details"));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const [detailSearch, setDetailSearch] = useState("");

  const [draftApplicationIds, setDraftApplicationIds] = useState([]);
  const [draftStatuses, setDraftStatuses] = useState(initialDefaultStatuses);
  const [draftAgingBuckets, setDraftAgingBuckets] = useState([]);
  const [draftTaskTypes, setDraftTaskTypes] = useState(initialDefaultTaskTypes);

  const [appliedApplicationIds, setAppliedApplicationIds] = useState([]);
  const [appliedStatuses, setAppliedStatuses] = useState(initialDefaultStatuses);
  const [appliedAgingBuckets, setAppliedAgingBuckets] = useState([]);
  const [appliedTaskTypes, setAppliedTaskTypes] = useState(initialDefaultTaskTypes);

  const [heatmapDrillDown, setHeatmapDrillDown] = useState(null);
  const skipNextAutoLoadRef = useRef(false);

  useEffect(() => {
    const loadLookups = async () => {
      let apps = parseLookupFromStorage("application_details");
      let statusList = parseLookupFromStorage("task_status_details");
      let taskTypeList = parseLookupFromStorage("task_type_details");

      if (!apps.length || !statusList.length || !taskTypeList.length) {
        try {
          const lookup = await TaskService.fetchLookupMasterData();
          if (Array.isArray(lookup?.application_details)) apps = lookup.application_details;
          if (Array.isArray(lookup?.task_status_details)) statusList = lookup.task_status_details;
          if (Array.isArray(lookup?.task_type_details)) taskTypeList = lookup.task_type_details;
        } catch (err) {
          console.warn("Could not load lookup master data for ageing report:", err);
        }
      }

      setApplications(apps);
      setStatuses(statusList);
      setTaskTypes(taskTypeList);
    };

    loadLookups();
  }, []);

  useEffect(() => {
    if (!statuses.length) return;
    const defaults = resolveDefaultStatuses(statuses);
    if (!defaults.length) return;
    setDraftStatuses((prev) => (prev.length ? prev : defaults));
    setAppliedStatuses((prev) => (prev.length ? prev : defaults));
  }, [statuses]);

  useEffect(() => {
    if (!taskTypes.length) return;
    const defaults = resolveDefaultTaskTypes(taskTypes);
    if (!defaults.length) return;
    setDraftTaskTypes((prev) => (prev.length ? prev : defaults));
    setAppliedTaskTypes((prev) => (prev.length ? prev : defaults));
  }, [taskTypes]);

  const apiFilters = useMemo(
    () =>
      buildApiFilters(
        appliedApplicationIds,
        appliedStatuses,
        appliedAgingBuckets,
        appliedTaskTypes,
        heatmapDrillDown
      ),
    [appliedApplicationIds, appliedStatuses, appliedAgingBuckets, appliedTaskTypes, heatmapDrillDown]
  );

  const applicationFilterOptions = useMemo(
    () =>
      applications.map((app) => ({
        value: String(app.aa_app_rl_application_id),
        label: app.aa_app_rl_application_name || `Application ${app.aa_app_rl_application_id}`,
      })),
    [applications]
  );

  const statusFilterOptions = useMemo(
    () =>
      statuses.map((status) => ({
        value: status.aa_app_rl_task_status_name,
        label: status.aa_app_rl_task_status_name,
      })),
    [statuses]
  );

  const agingBucketFilterOptions = useMemo(
    () =>
      AGING_BUCKETS.map((bucket) => ({
        value: bucket.code,
        label: bucket.displayLabel,
      })),
    []
  );

  const taskTypeFilterOptions = useMemo(() => {
    const map = new Map();
    (taskTypes || []).forEach((tt) => {
      const shortName = String(tt?.aa_app_rl_task_type_shortname || "").trim();
      const longName = String(tt?.aa_app_rl_task_type_longname || "").trim();
      const typeId = tt?.aa_app_rl_task_type_id;
      const value = shortName || longName || (typeId != null ? String(typeId) : "");
      if (!value) return;

      const label =
        shortName && longName && shortName.toLowerCase() !== longName.toLowerCase()
          ? `${longName} (${shortName})`
          : longName || shortName || `Type ${typeId}`;

      const key = value.toLowerCase();
      if (!map.has(key)) {
        map.set(key, { value, label });
      }
    });

    return Array.from(map.values()).sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
    );
  }, [taskTypes]);

  const loadReport = useCallback(
    async (forceRefresh = false, filtersOverride = null) => {
      const filters = filtersOverride ?? apiFilters;
      try {
        setLoading(true);
        setError(null);
        const [summary, details] = await Promise.all([
          TaskService.fetchTicketAgingReport(filters, forceRefresh),
          TaskService.fetchTicketAgingReportDetails(filters, forceRefresh),
        ]);
        setSummaryRows(Array.isArray(summary) ? summary : []);
        setDetailRows(Array.isArray(details) ? details : []);
      } catch (err) {
        console.error("Error loading ticket ageing report:", err);
        setError("Failed to load ticket ageing report. Please try again.");
        setSummaryRows([]);
        setDetailRows([]);
      } finally {
        setLoading(false);
      }
    },
    [apiFilters]
  );

  useEffect(() => {
    if (skipNextAutoLoadRef.current) {
      skipNextAutoLoadRef.current = false;
      return;
    }
    loadReport();
  }, [loadReport]);

  const { totals: bucketTotals, totalTickets } = useMemo(
    () => aggregateBucketTotals(summaryRows),
    [summaryRows]
  );

  const donutGradient = useMemo(() => {
    if (!totalTickets) return "conic-gradient(#e2e8f0 0deg, #e2e8f0 360deg)";
    let cursor = 0;
    const stops = [];
    for (const bucket of AGING_BUCKETS) {
      const pct = getBucketPercent(bucketTotals[bucket.code], totalTickets);
      if (pct <= 0) continue;
      const start = cursor * 3.6;
      cursor += pct;
      const end = cursor * 3.6;
      stops.push(`${BUCKET_COLORS[bucket.code]} ${start}deg ${end}deg`);
    }
    return stops.length ? `conic-gradient(${stops.join(", ")})` : "conic-gradient(#e2e8f0 0deg, #e2e8f0 360deg)";
  }, [bucketTotals, totalTickets]);

  const applicationChips = useMemo(() => {
    if (
      applicationFilterOptions.length > 0 &&
      draftApplicationIds.length === applicationFilterOptions.length
    ) {
      return [];
    }
    return draftApplicationIds.map((id) => {
      const app = applications.find((a) => String(a.aa_app_rl_application_id) === String(id));
      return {
        key: `app-${id}`,
        label: app?.aa_app_rl_application_name || `App ${id}`,
        onRemove: () =>
          setDraftApplicationIds((prev) => prev.filter((item) => item !== String(id))),
      };
    });
  }, [draftApplicationIds, applications, applicationFilterOptions]);

  const statusChips = useMemo(() => {
    if (statusFilterOptions.length > 0 && draftStatuses.length === statusFilterOptions.length) {
      return [];
    }
    return draftStatuses.map((status) => ({
      key: `status-${status}`,
      label: status,
      onRemove: () => setDraftStatuses((prev) => prev.filter((item) => item !== status)),
    }));
  }, [draftStatuses, statusFilterOptions]);

  const bucketChips = useMemo(() => {
    if (
      agingBucketFilterOptions.length > 0 &&
      draftAgingBuckets.length === agingBucketFilterOptions.length
    ) {
      return [];
    }
    return draftAgingBuckets.map((code) => {
      const bucket = AGING_BUCKETS.find((b) => b.code === code);
      return {
        key: `bucket-${code}`,
        label: bucket?.displayLabel || code,
        onRemove: () =>
          setDraftAgingBuckets((prev) => prev.filter((item) => item !== code)),
      };
    });
  }, [draftAgingBuckets, agingBucketFilterOptions]);

  const taskTypeChips = useMemo(() => {
    if (
      taskTypeFilterOptions.length > 0 &&
      draftTaskTypes.length === taskTypeFilterOptions.length
    ) {
      return [];
    }
    return draftTaskTypes.map((taskType) => {
      const option = taskTypeFilterOptions.find((opt) => opt.value === taskType);
      return {
        key: `task-type-${taskType}`,
        label: option?.label || taskType,
        onRemove: () =>
          setDraftTaskTypes((prev) => prev.filter((item) => item !== taskType)),
      };
    });
  }, [draftTaskTypes, taskTypeFilterOptions]);

  const filteredDetailRows = useMemo(() => {
    const q = detailSearch.trim().toLowerCase();
    if (!q) return detailRows;
    return detailRows.filter((row) => {
      const haystack = [
        row.aa_app_rl_resource_task_uid,
        row.aa_app_rl_resource_task_title,
        row.aa_app_rl_application_name,
        row.aa_app_rl_task_status_name,
        row.aa_app_rl_task_type_longname,
        row.aa_app_rl_task_type_shortname,
        row.aa_app_rl_priority_name,
        row.aa_app_task_assigned_to_name,
        row.ticket_age_days,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [detailRows, detailSearch]);

  const handleBack = () => navigate("/");

  const handleOpenTask = (taskId) => {
    if (taskId) navigate(`/update-task/${taskId}`);
  };

  const handleBucketHeaderClick = (code) => {
    setHeatmapDrillDown(null);
    setAppliedAgingBuckets((prev) => toggleArrayValue(prev, code));
    setDraftAgingBuckets((prev) => toggleArrayValue(prev, code));
  };

  const handleApplicationRowClick = (appId) => {
    setHeatmapDrillDown(null);
    const id = String(appId);
    setAppliedApplicationIds((prev) => toggleArrayValue(prev, id));
    setDraftApplicationIds((prev) => toggleArrayValue(prev, id));
  };

  const handleSummaryCellClick = (appId, bucketCode, count, appName) => {
    if (!Number(count)) return;
    const appIdStr = String(appId);
    const bucket = AGING_BUCKETS.find((b) => b.code === bucketCode);
    if (
      heatmapDrillDown?.appId === appIdStr &&
      heatmapDrillDown?.bucketCode === bucketCode
    ) {
      setHeatmapDrillDown(null);
      return;
    }
    setHeatmapDrillDown({
      appId: appIdStr,
      bucketCode,
      appName: appName || "Unknown",
      bucketLabel: bucket?.displayLabel || bucketCode,
    });
    setDetailsExpanded(true);
  };

  const handleClearHeatmapDrill = () => {
    setHeatmapDrillDown(null);
  };

  const handleRefresh = useCallback(() => {
    const statusDefaults = resolveDefaultStatuses(statuses);
    const taskTypeDefaults = resolveDefaultTaskTypes(taskTypes);
    setHeatmapDrillDown(null);
    const nextStatuses = statusDefaults.length ? statusDefaults : appliedStatuses;
    const nextTaskTypes = taskTypeDefaults.length ? taskTypeDefaults : appliedTaskTypes;
    const nextFilters = buildApiFilters(
      appliedApplicationIds,
      nextStatuses,
      appliedAgingBuckets,
      nextTaskTypes,
      null
    );
    if (statusDefaults.length || taskTypeDefaults.length) {
      skipNextAutoLoadRef.current = true;
    }
    if (statusDefaults.length) {
      setDraftStatuses(statusDefaults);
      setAppliedStatuses(statusDefaults);
    }
    if (taskTypeDefaults.length) {
      setDraftTaskTypes(taskTypeDefaults);
      setAppliedTaskTypes(taskTypeDefaults);
    }
    loadReport(true, nextFilters);
  }, [
    statuses,
    taskTypes,
    appliedApplicationIds,
    appliedAgingBuckets,
    appliedStatuses,
    appliedTaskTypes,
    loadReport,
  ]);

  const handleSearchFilters = () => {
    setHeatmapDrillDown(null);
    setAppliedApplicationIds([...draftApplicationIds]);
    setAppliedStatuses([...draftStatuses]);
    setAppliedAgingBuckets([...draftAgingBuckets]);
    setAppliedTaskTypes([...draftTaskTypes]);
  };

  const handleClearFilters = () => {
    setDraftApplicationIds([]);
    setDraftStatuses([]);
    setDraftAgingBuckets([]);
    setDraftTaskTypes([]);
    setAppliedApplicationIds([]);
    setAppliedStatuses([]);
    setAppliedAgingBuckets([]);
    setAppliedTaskTypes([]);
    setHeatmapDrillDown(null);
  };

  if (error && summaryRows.length === 0 && detailRows.length === 0 && !loading) {
    return (
      <div className="ticket-ageing-report">
        <div className="ticket-ageing-report__error">
          <p>{error}</p>
          <button type="button" className="btn btn-primary" onClick={() => loadReport(true)}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`ticket-ageing-report${loading ? " ticket-ageing-report--busy" : ""}`}
      aria-busy={loading}
    >
      <div className="ticket-ageing-report__hero">
        <div className="ticket-ageing-report__hero-text">
          <span className="ticket-ageing-report__eyebrow">Analytics</span>
          <h1 className="ticket-ageing-report__title">Ticket Ageing Report</h1>
          <p className="ticket-ageing-report__subtitle">
            Visual aging spectrum and application heatmap — filter, drill down, and open tickets instantly.
          </p>
        </div>
        <div className="ticket-ageing-report__hero-actions">
          <button type="button" className="btn btn-secondary" onClick={handleBack}>
            <i className="fas fa-arrow-left" aria-hidden="true" />
            Back
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleRefresh}
            disabled={loading}
          >
            <i className={`fas fa-sync-alt ${loading ? "fa-spin" : ""}`} aria-hidden="true" />
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      <div className="aging-command-deck" aria-label="Report controls">
        <div className="aging-command-deck__filters">
          <MultiSelectFilter
            id="ageing-app-filter"
            label="Application"
            icon="fa-cube"
            options={applicationFilterOptions}
            selectedValues={draftApplicationIds}
            onChange={setDraftApplicationIds}
            placeholder="All applications"
            chips={applicationChips}
          />

          <MultiSelectFilter
            id="ageing-status-filter"
            label="Status"
            icon="fa-tag"
            options={statusFilterOptions}
            selectedValues={draftStatuses}
            onChange={setDraftStatuses}
            placeholder="All statuses"
            chips={statusChips}
          />

          <MultiSelectFilter
            id="ageing-bucket-filter"
            label="Age bucket"
            icon="fa-hourglass-half"
            options={agingBucketFilterOptions}
            selectedValues={draftAgingBuckets}
            onChange={setDraftAgingBuckets}
            placeholder="All buckets"
            chips={bucketChips}
          />

          <MultiSelectFilter
            id="ageing-task-type-filter"
            label="Task type"
            icon="fa-layer-group"
            options={taskTypeFilterOptions}
            selectedValues={draftTaskTypes}
            onChange={setDraftTaskTypes}
            placeholder="All task types"
            chips={taskTypeChips}
          />

          <div className="aging-command-deck__actions">
            <button
              type="button"
              className="btn btn-primary aging-command-deck__search"
              onClick={handleSearchFilters}
              disabled={loading}
            >
              <i className="fas fa-search" aria-hidden="true" />
              Search
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleClearFilters}>
              Clear
            </button>
          </div>
        </div>
      </div>

      <section className="aging-spectrum" aria-labelledby="aging-spectrum-title">
        <div className="aging-spectrum__intro">
          <div>
            <h2 id="aging-spectrum-title" className="aging-spectrum__title">Aging spectrum</h2>
            <p className="aging-spectrum__hint">
              Fresh tickets on the left → critical backlog on the right. Tap a lane to filter.
            </p>
          </div>
          <div className="aging-spectrum__legend">
            <span className="aging-spectrum__legend-item aging-spectrum__legend-item--fresh">Fresh</span>
            <span className="aging-spectrum__legend-arrow" aria-hidden="true">→</span>
            <span className="aging-spectrum__legend-item aging-spectrum__legend-item--critical">Critical</span>
          </div>
        </div>

        <div className="aging-spectrum__body">
          <div className="aging-spectrum__donut-wrap" aria-label={`${totalTickets} total open tickets`}>
            <div className="aging-spectrum__donut" style={{ background: donutGradient }}>
              <div className="aging-spectrum__donut-hole">
                <strong>{totalTickets}</strong>
                <span>open</span>
              </div>
            </div>
          </div>

          <div className="aging-spectrum__lanes" role="list" aria-label="Ticket count by age bucket">
            {AGING_BUCKETS.map((bucket) => {
              const count = bucketTotals[bucket.code];
              const pct = getBucketPercent(count, totalTickets);
              const isActive = appliedAgingBuckets.includes(bucket.code);
              const laneHeight = totalTickets ? Math.max(18, Math.round((count / totalTickets) * 100)) : 0;
              return (
                <button
                  key={bucket.code}
                  type="button"
                  role="listitem"
                  className={`aging-lane ${bucket.className} ${isActive ? "aging-lane--active" : ""}`}
                  onClick={() => handleBucketHeaderClick(bucket.code)}
                  aria-pressed={isActive}
                  style={{ "--lane-height": `${laneHeight}%` }}
                >
                  <span className="aging-lane__bar" aria-hidden="true" />
                  <span className="aging-lane__count">{count}</span>
                  <span className="aging-lane__label">{bucket.displayLabel}</span>
                  <span className="aging-lane__pct">{pct}%</span>
                </button>
              );
            })}
          </div>
        </div>

        {appliedAgingBuckets.length > 0 && !heatmapDrillDown ? (
          <button
            type="button"
            className="aging-spectrum__clear btn btn-secondary"
            onClick={() => {
              setAppliedAgingBuckets([]);
              setDraftAgingBuckets([]);
            }}
          >
            <i className="fas fa-times" aria-hidden="true" />
            Clear lane filter
          </button>
        ) : null}
      </section>

      <section className="aging-heatmap-panel" aria-labelledby="aging-heatmap-title">
        <div className="aging-heatmap-panel__head">
          <h2 id="aging-heatmap-title" className="aging-heatmap-panel__title">
            Application heatmap
          </h2>
          <p className="aging-heatmap-panel__hint">
            Lighter tint = fewer tickets · Richer color = more tickets · Click a cell to drill down
          </p>
        </div>

        {heatmapDrillDown ? (
          <div className="aging-heatmap-drill" role="status">
            <div className="aging-heatmap-drill__text">
              <i className="fas fa-crosshairs" aria-hidden="true" />
              <span>
                Heatmap drill-down:{" "}
                <strong>{heatmapDrillDown.appName}</strong>
                {" · "}
                <strong>{heatmapDrillDown.bucketLabel}</strong>
              </span>
            </div>
            <button
              type="button"
              className="btn btn-secondary aging-heatmap-drill__clear"
              onClick={handleClearHeatmapDrill}
            >
              <i className="fas fa-times" aria-hidden="true" />
              Remove drill-down
            </button>
          </div>
        ) : null}

        {summaryRows.length === 0 ? (
          <div className="ticket-ageing-report__empty">
            <i className="fas fa-th aging-empty-icon" aria-hidden="true" />
            <p>No summary data for the selected filters.</p>
          </div>
        ) : (
          <div className="aging-heatmap-wrap">
            <div
              className="aging-heatmap"
              style={{
                gridTemplateColumns: `minmax(150px, 1.4fr) repeat(${AGING_BUCKETS.length}, minmax(52px, 1fr)) minmax(48px, 0.6fr)`,
              }}
            >
              <div className="aging-heatmap__corner">Application</div>
              {AGING_BUCKETS.map((bucket) => (
                <button
                  key={bucket.code}
                  type="button"
                  className={`aging-heatmap__col-head ${bucket.className} ${
                    appliedAgingBuckets.includes(bucket.code) ? "aging-heatmap__col-head--active" : ""
                  }`}
                  onClick={() => handleBucketHeaderClick(bucket.code)}
                >
                  <span>{bucket.shortLabel}</span>
                </button>
              ))}
              <div className="aging-heatmap__col-head aging-heatmap__col-head--total">Total</div>

              {summaryRows.map((row) => {
                const appId = row.aa_app_rl_application_id;
                const appName = row.aa_app_rl_application_name || "Unknown";
                const isAppActive = heatmapDrillDown
                  ? heatmapDrillDown.appId === String(appId)
                  : appliedApplicationIds.includes(String(appId));
                return (
                  <React.Fragment key={appId}>
                    <button
                      type="button"
                      className={`aging-heatmap__row-head ${isAppActive ? "aging-heatmap__row-head--active" : ""}`}
                      onClick={() => handleApplicationRowClick(appId)}
                    >
                      <span className="aging-heatmap__monogram">{getAppMonogram(appName)}</span>
                      <HeatmapAppName name={appName} />
                    </button>
                    {AGING_BUCKETS.map((bucket) => {
                      const count = Number(row[bucket.code] || 0);
                      const heatColor = getHeatmapBucketColor(bucket.code);
                      const useLightText = HEATMAP_BUCKET_USE_LIGHT_TEXT[bucket.code];
                      const isCellActive =
                        heatmapDrillDown?.appId === String(appId) &&
                        heatmapDrillDown?.bucketCode === bucket.code &&
                        count > 0;
                      return (
                        <button
                          key={`${appId}-${bucket.code}`}
                          type="button"
                          className={`aging-heatmap__cell ${
                            isCellActive ? "aging-heatmap__cell--active" : ""
                          } ${count ? "" : "aging-heatmap__cell--zero"} ${
                            useLightText ? "aging-heatmap__cell--hot" : ""
                          }`}
                          style={
                            count
                              ? {
                                  "--heat-color": heatColor,
                                  background: heatColor,
                                  color: useLightText ? "#fff" : "#14532d",
                                }
                              : undefined
                          }
                          onClick={() => handleSummaryCellClick(appId, bucket.code, count, appName)}
                          disabled={!count}
                          title={
                            count
                              ? `${appName} · ${bucket.displayLabel}: ${count} tickets`
                              : "No tickets"
                          }
                        >
                          {count > 0 ? count : ""}
                        </button>
                      );
                    })}
                    <div className="aging-heatmap__cell aging-heatmap__cell--row-total">
                      {row.total_tickets ?? "0"}
                    </div>
                  </React.Fragment>
                );
              })}

              <div className="aging-heatmap__footer-label">Total</div>
              {AGING_BUCKETS.map((bucket) => (
                <div key={`total-${bucket.code}`} className="aging-heatmap__cell aging-heatmap__cell--footer">
                  {bucketTotals[bucket.code]}
                </div>
              ))}
              <div className="aging-heatmap__cell aging-heatmap__cell--footer aging-heatmap__cell--grand-total">
                {totalTickets}
              </div>
            </div>
          </div>
        )}
      </section>

      <section className="aging-panel aging-panel--details" aria-labelledby="aging-details-title">
        <button
          type="button"
          className="aging-details-toggle"
          id="aging-details-title"
          onClick={() => setDetailsExpanded((v) => !v)}
          aria-expanded={detailsExpanded}
        >
          <span className="aging-details-toggle__left">
            <span className="aging-details-toggle__icon-wrap" aria-hidden="true">
              <i className="fas fa-layer-group" />
            </span>
            <span className="aging-details-toggle__text">
              <span className="aging-details-toggle__label">Ticket Details</span>
              <span className="aging-details-toggle__sub">
                Interactive ticket cards · click to open
              </span>
            </span>
            <span className="aging-details-toggle__count">{detailRows.length}</span>
          </span>
          <i
            className={`fas fa-chevron-${detailsExpanded ? "up" : "down"} aging-details-toggle__chevron`}
            aria-hidden="true"
          />
        </button>

        {detailsExpanded && (
          <div className="aging-details-body">
            {detailRows.length > 0 && (
              <div className="aging-details-toolbar">
                <div className="aging-details-search">
                  <i className="fas fa-search" aria-hidden="true" />
                  <input
                    type="search"
                    value={detailSearch}
                    onChange={(e) => setDetailSearch(e.target.value)}
                    placeholder="Search tickets by ID, title, app, status, assignee..."
                    aria-label="Search ticket details"
                  />
                  {detailSearch && (
                    <button
                      type="button"
                      className="aging-details-search__clear"
                      onClick={() => setDetailSearch("")}
                      aria-label="Clear search"
                    >
                      <i className="fas fa-times" aria-hidden="true" />
                    </button>
                  )}
                </div>
                <span className="aging-details-toolbar__meta">
                  Showing {filteredDetailRows.length} of {detailRows.length}
                </span>
              </div>
            )}

            {detailRows.length === 0 ? (
              <div className="ticket-ageing-report__empty aging-details-empty">
                <i className="fas fa-inbox aging-empty-icon" aria-hidden="true" />
                <p>No tickets match the selected filters.</p>
              </div>
            ) : filteredDetailRows.length === 0 ? (
              <div className="ticket-ageing-report__empty aging-details-empty">
                <i className="fas fa-search aging-empty-icon" aria-hidden="true" />
                <p>No tickets match your search.</p>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setDetailSearch("")}
                >
                  Clear search
                </button>
              </div>
            ) : (
              <div className="ticket-detail-grid">
                {filteredDetailRows.map((row) => {
                  const taskId = row.aa_app_rl_resource_task_uid;
                  const ageDays = row.ticket_age_days;
                  const bucketClass = getBucketClassForDays(ageDays);
                  const priorityClass = getPriorityClass(row.aa_app_rl_priority_name);
                  const assignedTo = row.aa_app_task_assigned_to_name || "Unassigned";
                  const assigneeNames = getAssigneeNames(assignedTo);
                  const ringProgress = getAgeRingProgress(ageDays);

                  return (
                    <article
                      key={taskId}
                      className={`ticket-detail-card ${bucketClass}`}
                      onClick={() => handleOpenTask(taskId)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleOpenTask(taskId);
                        }
                      }}
                      tabIndex={0}
                      role="button"
                      aria-label={`Open ticket ${taskId}: ${row.aa_app_rl_resource_task_title || "Untitled"}`}
                    >
                      <div className="ticket-detail-card__glow" aria-hidden="true" />
                      <div className="ticket-detail-card__accent" aria-hidden="true" />

                      <div className="ticket-detail-card__header">
                        <span className="ticket-detail-card__id">#{taskId}</span>
                        <div
                          className="ticket-detail-card__age-ring"
                          style={{ "--ring-progress": `${ringProgress}%` }}
                          title={getBucketLabelForDays(ageDays)}
                        >
                          <svg viewBox="0 0 36 36" className="ticket-detail-card__ring-svg" aria-hidden="true">
                            <circle className="ticket-detail-card__ring-track" cx="18" cy="18" r="15.5" />
                            <circle className="ticket-detail-card__ring-fill" cx="18" cy="18" r="15.5" />
                          </svg>
                          <div className="ticket-detail-card__ring-label">
                            <strong>{ageDays ?? "—"}</strong>
                            <small>days</small>
                          </div>
                        </div>
                      </div>

                      <h3 className="ticket-detail-card__title">
                        {row.aa_app_rl_resource_task_title || "Untitled"}
                      </h3>

                      <div className="ticket-detail-card__assignee">
                        <span className="ticket-detail-card__assignee-label">Assigned to</span>
                        {assigneeNames.length === 0 ? (
                          <div className="ticket-detail-card__assignee-members">
                            <span className="ticket-detail-card__avatar" aria-hidden="true">
                              ?
                            </span>
                            <span className="ticket-detail-card__assignee-name">Unassigned</span>
                          </div>
                        ) : (
                          <div className="ticket-detail-card__assignee-members">
                            <div className="ticket-detail-card__avatar-stack" aria-hidden="true">
                              {assigneeNames.slice(0, 3).map((member) => (
                                <span key={member} className="ticket-detail-card__avatar" title={member}>
                                  {getInitials(member)}
                                </span>
                              ))}
                              {assigneeNames.length > 3 && (
                                <span className="ticket-detail-card__avatar ticket-detail-card__avatar--more">
                                  +{assigneeNames.length - 3}
                                </span>
                              )}
                            </div>
                            <span className="ticket-detail-card__assignee-name">{assignedTo}</span>
                          </div>
                        )}
                      </div>

                      <div className="ticket-detail-card__chips">
                        <span className="ticket-detail-chip ticket-detail-chip--app">
                          <i className="fas fa-cube" aria-hidden="true" />
                          {row.aa_app_rl_application_name || "Unknown"}
                        </span>
                        <span className="ticket-detail-chip ticket-detail-chip--status">
                          {row.aa_app_rl_task_status_name || "Unknown"}
                        </span>
                        <span
                          className="ticket-detail-chip ticket-detail-chip--type"
                          title={row.aa_app_rl_task_type_longname || row.aa_app_rl_task_type_shortname || "Unknown"}
                        >
                          {row.aa_app_rl_task_type_shortname || row.aa_app_rl_task_type_longname || "Unknown"}
                        </span>
                        <span className={`ticket-detail-chip ticket-detail-chip--priority ticket-detail-chip--priority-${priorityClass}`}>
                          {row.aa_app_rl_priority_name || "Unknown"}
                        </span>
                      </div>

                      <div className="ticket-detail-card__meta">
                        <div className="ticket-detail-card__meta-item">
                          <i className="fas fa-calendar-plus" aria-hidden="true" />
                          <span>Created {formatDate(row.aa_app_task_creation_date)}</span>
                        </div>
                        <div className="ticket-detail-card__meta-item">
                          <i className="fas fa-flag" aria-hidden="true" />
                          <span>Due {formatDate(row.aa_app_task_due_date)}</span>
                        </div>
                      </div>

                      <div className="ticket-detail-card__footer">
                        <span className="ticket-detail-card__open">
                          Open
                          <i className="fas fa-arrow-right" aria-hidden="true" />
                        </span>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </section>

      <p className="ticket-ageing-report__footer">
        {summaryRows.length} application(s) · {detailRows.length} ticket(s)
        {loading ? " · Updating..." : ""}
      </p>

      {loading ? (
        <div
          className="aging-report-loading-overlay"
          role="status"
          aria-live="polite"
          aria-label="Loading ticket ageing report"
        >
          <div className="aging-report-loading-overlay__panel">
            <div className="aging-report-loading-overlay__spinner" aria-hidden="true" />
            <p className="aging-report-loading-overlay__text">
              {summaryRows.length === 0 && detailRows.length === 0
                ? "Loading ticket ageing report..."
                : "Applying filters..."}
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default TicketAgeingReportScreen;
